import * as React from "react";

import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { Pie, PieChart as RechartsPieChart, ResponsiveContainer, Sector, Tooltip } from "@/ui-adapters/Recharts";
import { RuntimeColorSwatch } from "@/ui-adapters/RuntimeColorBadge";
import { RuntimeSquareSize } from "@/ui-adapters/RuntimeSquareSize";

import { dataColors } from "./theme";

interface Props {
  size: number;
  slices: { amount: number; label: string; hoverLabel?: React.ReactNode }[];
  hoverLabel?: (data: { label: string; amount: number }) => React.ReactNode;
}

const colors = [
  dataColors.primary,
  dataColors.pieChartGrey,
  dataColors.benchmark,
  dataColors.pieChartGrey,
  dataColors.pieChartDarkGrey,
  dataColors.benchmark,
  dataColors.pieChartDark,
];

export function PieChart({ slices, size, hoverLabel }: Props): React.ReactNode {
  const chartData = React.useMemo(
    () =>
      slices
        .map((slice, i) => ({
          name: slice.label,
          value: slice.amount,
          fill: colors[i % colors.length],
        }))
        .filter((d) => d.value > 0),
    [slices],
  );

  return (
    <Inline gap="md" align="center">
      <RuntimeSquareSize size={size}>
        {(attributes) => (
          <figure {...attributes}>
            <ResponsiveContainer>
              <RechartsPieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={size / 3}
                  label={false}
                  shape={(props) => <Sector {...props} stroke={props.fill} />}
                />
                {hoverLabel && (
                  <Tooltip content={<CustomTooltip hoverLabel={hoverLabel} />} cursor={{ fill: "transparent" }} />
                )}
              </RechartsPieChart>
            </ResponsiveContainer>
          </figure>
        )}
      </RuntimeSquareSize>

      <Stack gap="sm">
        {slices.map((slice, i) => (
          <Inline key={slice.label} gap="sm" align="center" wrap={false}>
            <RuntimeColorSwatch color={colors[i % colors.length]} size="compact" />
            <span className="text-body text-text-primary">{slice.label}</span>
          </Inline>
        ))}
      </Stack>
    </Inline>
  );
}

function CustomTooltip({
  active,
  payload,
  hoverLabel,
}: Partial<{
  active?: boolean;
  payload?: { name: string; value: number }[];
}> & {
  hoverLabel: Exclude<Props["hoverLabel"], undefined>;
}) {
  const first = payload?.[0];
  if (!active || !first) {
    return null;
  }

  return (
    <ChartTooltip>
      {hoverLabel({
        label: first.name,
        amount: first.value || 0,
      })}
    </ChartTooltip>
  );
}
